import React from "react";
import "./BrowseGrid.css";
import "./BrowseCard.scss";
import { useAppContext } from "../hooks/useAppContext";
import { createOrderItems, getCartToken } from "../services/cart.service";
import useToast from "../hooks/useToast";
import {
  fetchFilteredFunds,
  fetchFundsByCategoryId,
  setCampaignPath,
} from "../services/funds.service";

function FundList({
  fundsPage,
  setFundsPage,
  fundsPager,
  setFundsPager,
  fundCategoryId,
  categoryInfo,
}) {
  const [itemIds, setItemIds] = React.useState([]);
  const {
    search,
    funds,
    items,
    setFunds,
    setLoadOrderItemList,
    openMenu,
    frequency,
    isLoading,
    setIsLoading,
    setEditStateItems,
    editStateItems,
    setIsLoadingNewItem,
  } = useAppContext();

  let next = null;
  const itemsPerPage = 9;
  const loadMore = fundsPage > 0;

  function getNotificationError(id, action, message) {
    return useToast({
      type: "error",
      id,
      actionText: "RETRY?",
      action: async () => await action(),
      dismissable: true,
      message,
    });
  }

  function getFilteredFunds(loadMore) {
    const notifyFetchFilteredFundsError = getNotificationError(
      "filteredFunds",
      () => getFilteredFunds(loadMore),
      "There was a problem loading search results."
    );
    setIsLoading(true);
    return fetchFilteredFunds(
      fundCategoryId,
      search,
      fundsPage,
      itemsPerPage,
      loadMore
    )
      .then((response) => {
        const { data, links } = response;
        setFundsPager(links.next !== undefined);
        if (fundsPage > 0) {
          // Add new funds to existing ones
          setFunds((funds) => [...funds, ...data]);
        } else {
          // Replace results by default.
          setFunds(data);
        }
        return data;
      })
      .catch((e) => {
        notifyFetchFilteredFundsError(e);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function getFundsByCategoryId(loadMore) {
    const notifyFetchFundsByCategoryIdError = (err) =>
      getNotificationError(
        `fundsByCategory${fundCategoryId}`,
        () => getFundsByCategoryId(loadMore),
        `There was a problem loading funds in category ${fundCategoryId}. ${err.toString()}`
      );
    setIsLoading(true);
    return fetchFundsByCategoryId(fundCategoryId, fundsPage, loadMore)
      .then((response) => {
        setFunds((funds) => [
          ...((loadMore && funds) || []),
          ...response.results,
        ]);
        setFundsPager(response.pager);
        return true;
      })
      .catch((err) => {
        notifyFetchFundsByCategoryIdError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  // Reset the current page. We need to reset this when we click on back button for example.
  React.useEffect(() => {
    setFundsPage(0);
    window.scroll({top:0,behavior:'smooth'});
  }, [fundCategoryId, search]);

  React.useEffect(() => {
    if (search) {
      // Search.
      getFilteredFunds(loadMore);
    } else {
      // Fund List.
      getFundsByCategoryId(loadMore);
    }
  }, [fundCategoryId, search, fundsPage]);

  React.useEffect(() => {
    let ids = [];
    items.map((v, i) => {
      ids.push(v.relationships.purchased_entity.data.id);
    });

    setItemIds(ids);
  }, [items]);

  function addFundToCart(fund) {
    const notifyCreateOrderItemError = (err) =>
      getNotificationError(
        "createOrderItems",
        () => addFundToCart(fund),
        `There was a problem adding the fund to your cart. ${err.toString()}`
      );
    return createOrderItems({
      amt: "0.00",
      fund_id: fund.uuid ? fund.uuid : fund.id,
      frequency: frequency,
      category: categoryInfo.name,
    })
      .then((response) => {
        setCampaignPath(window.location.pathname);
        // Open the sidebar.
        openMenu();
        return response;
      })
      .finally(() => {
        setLoadOrderItemList(true);
        setIsLoading(false);
        setEditStateItems([...editStateItems, response[0].id]);
      })
      .catch((err) => {
        notifyCreateOrderItemError(err);
      });
  }

  function onClickDonate(fund) {
    setIsLoadingNewItem(true);
    return addFundToCart(fund);
  }

  const renderFunds = () =>
    funds.map((fund) => {
      const s = fund.field_background_image;
      let rex = /<img[^>]+src="([^">]+)/g;
      let r = rex.exec(s);
      const backgroundImage = r !== null ? r[1] : null;
      const inlineStyle = backgroundImage
        ? {
            backgroundImage: `url(${backgroundImage})`,
          }
        : {};
      const uuid = fund.uuid ? fund.uuid : fund.id;
      const attrs = fund.attributes ? fund.attributes : fund;

      const title = attrs.title;
      const type = attrs.field_donation_type;
      const description = attrs.description;
      const highlighted = attrs.field_highlighted;

      return (
        <div
          key={uuid}
          data-hook={`${type}-item`}
          className="browse__grid-item browse__grid-item--fund"
        >
          <div
            className={`browse-card browse-card--fund${
              parseInt(highlighted)
                ? " is-highlighted coh-style-container-theme---dark-1"
                : ""
            }${backgroundImage ? " has-background-image" : ""}`}
            style={inlineStyle}
          >
            <div className="browse-card__inner browse-card__inner--fund">
              <h3
                className={`browse-card__title browse-card__title--fund ${
                  description && " has-small-description"
                }`}
              >
                <span dangerouslySetInnerHTML={{ __html: title }} />
              </h3>
              {description && (
                <div className="browse-card__description browse-card__description--fund">
                  {description}
                </div>
              )}
              <div className="browse-card__actions browse-card__actions--fund">
                {itemIds.indexOf(uuid) !== -1 ? (
                  <button
                    className="browse-card__button browse-card__button--fund is-in-cart coh-style-link-button-with-icon"
                    onClick={openMenu}
                  >
                    Added
                  </button>
                ) : (
                  <button


                    data-hook="donate-btn"
                    className="browse-card__button browse-card__button--fund coh-style-link-button-with-icon"
                    onClick={(event) => {
                      event.currentTarget.disabled = true;
                      onClickDonate(fund);
                    }}
                  >
                    Give now
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    });

  // Get the next page.
  if (fundsPager) {
    if (typeof fundsPager === "object") {
      next = fundsPager.next_page > 0 ? fundsPager.next_page : null;
    } else {
      next = fundsPage + itemsPerPage;
    }
  }

  if (search) {
    return (
      <>
        <div className="browse__wrapper browse__wrapper--funds" id="funds">
          {funds.length > 0 ? (
            <h2
              className={`browse__title browse__title--funds${
                isLoading ? " spinner--after spinner--small" : ""
              }`}
            >
              Search Results
            </h2>
          ) : null}
          {!isLoading ? (
            <div className="browse__grid browse__grid--funds">
              {funds.length > 0 ? (
                renderFunds()
              ) : (
                <div className="messages messages--warning">
                  No funds could be found that match your search.
                </div>
              )}
            </div>
          ) : null}
        </div>
        {next > 0 ? (
          <div className="browse__pagination">
            <button
              className="coh-style-link-button-hollow"
              onClick={() => {
                setFundsPage(next);
              }}
            >
              Load More
            </button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      {funds.length > 0 ? (
        <div className="browse__wrapper browse__wrapper--funds" id="funds">
          <h2
            className={`browse__title browse__title--funds${
              isLoading ? " spinner--after spinner--small" : ""
            }`}
          >
            Funds
          </h2>
          {!isLoading ? (
            <div className="browse__grid browse__grid--funds">
              {renderFunds()}
            </div>
          ) : null}
        </div>
      ) : null}
      {next > 0 ? (
        <div className="browse__pagination">
          <button
            className="coh-style-link-button-hollow"
            onClick={() => {
              setFundsPage(next);
            }}
          >
            Load More
          </button>
        </div>
      ) : null}
    </>
  );
}

export default FundList;
